import React from "react";
import { Link } from "react-router-dom";
import {
  Repeat,
  ScanFace,
  RotateCw,
  Zap,
  UserCheck,
  Radar,
  Languages,
  KeyRound,
  ArrowRight,
  ShieldCheck,
  Lock,
  FileCheck2,
  BadgeCheck,
  Fingerprint,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Eyebrow, Card } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { FeatureCard, CTASection } from "../../components/marketing/Shared";
import { useDemo } from "../../components/demo/DemoContext";

function HeroMockup() {
  const { t } = useI18n();
  return (
    <div className="relative">
      <div className="rounded-2xl border border-white/10 bg-neutral-900/80 p-4 shadow-2xl backdrop-blur">
        <div className="mb-3 flex items-center justify-between px-1">
          <span className="flex items-center gap-2 text-xs font-semibold text-white/80">
            <span className="h-2 w-2 animate-pulse rounded-full bg-brand" />
            {t("home.hero.panel")}
          </span>
          <span className="font-mono text-[10px] text-white/40">session · live</span>
        </div>

        {/* Face capture area */}
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/50">
          <div
            className="aspect-[4/3] w-full"
            style={{
              background:
                "radial-gradient(120% 90% at 50% 30%, hsl(var(--brand)/.10), transparent 60%), repeating-linear-gradient(135deg, rgba(255,255,255,.03) 0 8px, rgba(255,255,255,.06) 8px 16px)",
            }}
          />
          {/* face reticle */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative flex h-32 w-28 items-center justify-center rounded-[50%] border-2 border-brand/70">
              <ScanFace className="h-12 w-12 text-brand/80" />
              <span className="absolute inset-x-0 top-0 h-0.5 animate-pulse bg-brand/80" />
            </div>
          </div>
          {/* gesture prompt */}
          <div className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80">
            <RotateCw className="h-3 w-3 text-brand" /> turn your head →
          </div>
          {/* corner marks */}
          <span className="absolute right-2 top-2 rounded bg-warning/90 px-1.5 py-0.5 font-mono text-[9px] font-bold text-black">
            live
          </span>
        </div>

        {/* liveness meter */}
        <div className="mt-3 rounded-lg bg-black/40 px-3 py-2">
          <div className="flex items-center justify-between font-mono text-[10px] text-white/50">
            <span>liveness</span>
            <span className="text-emerald-400">0.98</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <span className="block h-full w-[98%] rounded-full bg-gradient-to-r from-brand to-emerald-400" />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between rounded-lg bg-black/40 px-3 py-2">
          <span className="font-mono text-[10px] text-emerald-400">● {t("home.hero.status")}</span>
          <span className="font-mono text-[10px] text-white/50">gesture ✓</span>
        </div>
      </div>

      <div className="absolute -bottom-4 -right-4 hidden items-center gap-2 rounded-xl border border-white/10 bg-neutral-900 px-3 py-2 shadow-xl sm:flex">
        <BadgeCheck className="h-5 w-5 text-emerald-400" />
        <div>
          <span className="block font-mono text-[10px] text-white/50">verdict</span>
          <p className="font-mono text-sm font-bold text-white">passed</p>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();
  const { openDemo } = useDemo();

  const features = [
    { icon: Repeat, k: "redirect" },
    { icon: Radar, k: "pad" },
    { icon: RotateCw, k: "gesture" },
    { icon: Zap, k: "flash" },
    { icon: UserCheck, k: "facematch" },
    { icon: Fingerprint, k: "deep" },
    { icon: Languages, k: "multilang" },
    { icon: KeyRound, k: "api" },
  ];

  const steps = ["s1", "s2", "s3", "s4"];

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-neutral-950 text-white">
        <div className="absolute inset-0 cyber-grid" />
        <div
          className="pointer-events-none absolute -top-40 right-0 h-[36rem] w-[36rem] rounded-full blur-3xl"
          style={{ background: "hsl(var(--brand) / 0.16)" }}
        />
        <Container className="relative grid items-center gap-12 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
          <div>
            <Eyebrow>{t("home.hero.eyebrow")}</Eyebrow>
            <h1 className="mt-5 text-4xl font-black leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              {t("home.hero.title1")}
              <br />
              <span className="text-gradient-brand">{t("home.hero.titleHighlight")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">{t("home.hero.lead")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={openDemo}>
                {t("demo.cta")}
              </Button>
              <Button to="/contato" size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10">
                {t("home.hero.ctaDemo")}
              </Button>
            </div>
          </div>
          <HeroMockup />
        </Container>
      </section>

      {/* Use cases */}
      <Section>
        <Container>
          <SectionHead
            center
            eyebrow={t("home.verticals.eyebrow")}
            title={t("home.verticals.title")}
            subtitle={t("home.verticals.subtitle")}
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {[
              {
                to: "/onboarding",
                icon: UserCheck,
                accent: "exams",
                tag: t("home.verticals.onb.tag"),
                title: t("home.verticals.onb.title"),
                desc: t("home.verticals.onb.desc"),
                cta: t("home.verticals.onb.cta"),
              },
              {
                to: "/autenticacao",
                icon: ShieldCheck,
                accent: "ctf",
                tag: t("home.verticals.auth.tag"),
                title: t("home.verticals.auth.title"),
                desc: t("home.verticals.auth.desc"),
                cta: t("home.verticals.auth.cta"),
              },
            ].map((v) => (
              <Link
                key={v.to}
                to={v.to}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:shadow-lg"
                style={{ boxShadow: "inset 0 0 0 1px transparent" }}
              >
                <div
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl transition-opacity group-hover:opacity-100"
                  style={{ background: `hsl(var(--${v.accent}) / 0.14)`, opacity: 0.6 }}
                />
                <div
                  className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ background: `hsl(var(--${v.accent}) / 0.14)`, color: `hsl(var(--${v.accent}))` }}
                >
                  <v.icon className="h-7 w-7" />
                </div>
                <p
                  className="relative mt-5 font-mono text-xs font-bold uppercase tracking-wider"
                  style={{ color: `hsl(var(--${v.accent}))` }}
                >
                  {v.tag}
                </p>
                <h3 className="relative mt-2 text-2xl">{v.title}</h3>
                <p className="relative mt-3 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                <span
                  className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold"
                  style={{ color: `hsl(var(--${v.accent}))` }}
                >
                  {v.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section alt>
        <Container>
          <SectionHead
            center
            eyebrow={t("home.features.eyebrow")}
            title={t("home.features.title")}
            subtitle={t("home.features.subtitle")}
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard
                key={f.k}
                icon={f.icon}
                title={t(`feat.${f.k}.title`)}
                desc={t(`feat.${f.k}.desc`)}
              />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button to="/recursos" variant="outline">
              {t("common.learnMore")} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Container>
      </Section>

      {/* How it works */}
      <Section>
        <Container>
          <SectionHead center eyebrow={t("home.how.eyebrow")} title={t("home.how.title")} />
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s} className="relative">
                <span className="font-mono text-5xl font-black text-brand/25">{i + 1}</span>
                <h3 className="mt-2 text-lg">{t(`home.how.${s}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`home.how.${s}.desc`)}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Compliance */}
      <Section alt>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <SectionHead
                eyebrow={t("home.compliance.eyebrow")}
                title={t("home.compliance.title")}
                subtitle={t("home.compliance.subtitle")}
              />
              <Button to="/seguranca" variant="outline" className="mt-6">
                {t("common.learnMore")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: ShieldCheck, label: t("home.compliance.privacy") },
                { icon: BadgeCheck, label: t("home.compliance.minimization") },
                { icon: Lock, label: t("home.compliance.encryption") },
                { icon: FileCheck2, label: t("home.compliance.dpa") },
              ].map((c) => (
                <Card key={c.label} className="flex items-center gap-3 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/12 text-success">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold">{c.label}</span>
                </Card>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
